import { attr, belongsTo } from '@ember-data/model';
import { service } from '@ember/service';
import { Type } from '@warp-drive/core-types/symbols';
import moment from 'moment-timezone';
import BaseModel, { type IResourceName } from 'volta/models/base-model';
import { tasksToAgg } from 'volta/models/task';
import { formatLocalDate } from 'volta/utils/date-utils';
import { getBufferZoneCriticality } from 'volta/utils/style-utils';

import type { IntlService } from 'ember-intl';
import type Task from 'volta/models/task';
import type { IAggTasksViewMap } from 'volta/models/types/tasks';
import type LabelService from 'volta/services/label-service';
import type SkuModel from './sku';
import type { IExecutionBuffer } from './types/planning';
import { PLANNING_CALCULATION_STALE_HOURS } from './planning';

export const ResourceName: IResourceName = {
  singular: 'executionAlert',
  plural: 'executionAlerts'
};

export const Queries = {
  AlertsTs: 'alertsTs'
};

export default class ExecutionAlertModel extends BaseModel {
  @service intl!: IntlService;
  @service labelService!: LabelService;

  /**
   * Attributes
   */
  @attr('date') inventoryDate!: Date;
  @attr('date') dueDate?: Date;
  @attr('number') onHand?: number;
  @attr('number') openSupply?: number;
  @attr('number') demand?: number;
  @attr('number') avgUsage?: number;
  @attr('number') demandSpikeQty?: number;
  @attr('number') oha?: number;
  @attr('number') ohaZone?: number;
  @attr('number') projectedOha?: number;
  @attr('number') projectedOhaZone?: number;
  @attr('date') projectedOhaDate?: Date;
  @attr('number') projectedOhaSimulated?: boolean;
  @attr() buffer?: IExecutionBuffer;
  @attr() tasks?: IAggTasksViewMap;

  @belongsTo<SkuModel>('sku', { async: false, inverse: null })
  declare sku: SkuModel;

  /**
   * Checks if the product has a demand spike
   */
  get hasDemandSpike() {
    return this.demandSpikeQty && this.demandSpikeQty < 0;
  }

  get partCode() {
    return this.sku.part.code;
  }

  get skuDescription() {
    return this.sku.skuDescription;
  }

  get isMRP() {
    return this.sku.methodology === 'MRP';
  }

  /**
   * DO NOT REMOVE, it's used by the sku-details execution dashboard
   */
  get dueDateLabel() {
    const { dueDate } = this;
    return formatLocalDate(
      moment.utc(dueDate).add(dueDate ? 0 : (this.sku.actualLeadTime ?? 0), 'd')
    );
  }

  /**
   * Get the priority name from the buffer zone and penetration
   */
  get priorityName() {
    return getBufferZoneCriticality(this.ohaZone);
  }

  /**
   * Get the priority name from the buffer zone and penetration
   */
  get projectedPriorityName() {
    return getBufferZoneCriticality(this.projectedOhaZone);
  }

  /**
   * DO NOT REMOVE, it's used by the sku-details planning dashboard
   */
  get unitMeasureLabel() {
    return this.labelService.unitMeasure(this.sku.part.unitMeasure);
  }

  get priorityPercent() {
    return this.oha && this.oha * 100;
  }

  get isCalculationOld() {
    return moment().diff(moment(this.updatedAt), 'hours') > PLANNING_CALCULATION_STALE_HOURS;
  }

  setTasks(tasks: Task[]) {
    this.tasks = tasksToAgg(tasks);
  }

  [Type] = 'execution-alert' as const;
}
